import type {
  Event,
  EventCategory,
  EventContext,
  EventHandler,
  EventSeverity,
} from "./types";

/**
 * Simple logger class with handler support
 */
class Logger {
  private handlers: EventHandler[] = [];

  /**
   * Core logging function
   */
  log(
    category: EventCategory,
    severity: EventSeverity,
    message: string,
    context?: EventContext,
  ): void {
    const event: Event = {
      timestamp: Date.now(),
      category,
      severity,
      message,
      context,
    };

    // Dispatch to all registered handlers
    for (const handler of this.handlers) {
      try {
        handler(event);
      } catch (err) {
        // Prevent handler errors from breaking the app
        console.error("Event handler error:", err);
      }
    }
  }

  /**
   * Add an event handler
   */
  addHandler(handler: EventHandler): void {
    this.handlers.push(handler);
  }

  /**
   * Remove an event handler
   */
  removeHandler(handler: EventHandler): void {
    const index = this.handlers.indexOf(handler);
    if (index > -1) {
      this.handlers.splice(index, 1);
    }
  }

  /**
   * Convenience methods for different severity levels
   */
  debug(category: EventCategory, message: string, context?: EventContext): void {
    this.log(category, "debug", message, context);
  }

  info(category: EventCategory, message: string, context?: EventContext): void {
    this.log(category, "info", message, context);
  }

  warn(category: EventCategory, message: string, context?: EventContext): void {
    this.log(category, "warn", message, context);
  }

  error(category: EventCategory, message: string, context?: EventContext): void {
    this.log(category, "error", message, context);
  }

  /**
   * Category-specific loggers for convenience
   */
  readonly auth = {
    debug: (message: string, context?: EventContext) =>
      this.debug("auth", message, context),
    info: (message: string, context?: EventContext) =>
      this.info("auth", message, context),
    warn: (message: string, context?: EventContext) =>
      this.warn("auth", message, context),
    error: (message: string, context?: EventContext) =>
      this.error("auth", message, context),
  };

  readonly sync = {
    debug: (message: string, context?: EventContext) =>
      this.debug("sync", message, context),
    info: (message: string, context?: EventContext) =>
      this.info("sync", message, context),
    warn: (message: string, context?: EventContext) =>
      this.warn("sync", message, context),
    error: (message: string, context?: EventContext) =>
      this.error("sync", message, context),
  };

  readonly storage = {
    debug: (message: string, context?: EventContext) =>
      this.debug("storage", message, context),
    info: (message: string, context?: EventContext) =>
      this.info("storage", message, context),
    warn: (message: string, context?: EventContext) =>
      this.warn("storage", message, context),
    error: (message: string, context?: EventContext) =>
      this.error("storage", message, context),
  };

  readonly ui = {
    debug: (message: string, context?: EventContext) =>
      this.debug("ui", message, context),
    info: (message: string, context?: EventContext) => this.info("ui", message, context),
    warn: (message: string, context?: EventContext) => this.warn("ui", message, context),
    error: (message: string, context?: EventContext) =>
      this.error("ui", message, context),
  };

  readonly network = {
    debug: (message: string, context?: EventContext) =>
      this.debug("network", message, context),
    info: (message: string, context?: EventContext) =>
      this.info("network", message, context),
    warn: (message: string, context?: EventContext) =>
      this.warn("network", message, context),
    error: (message: string, context?: EventContext) =>
      this.error("network", message, context),
  };

  readonly srs = {
    debug: (message: string, context?: EventContext) =>
      this.debug("srs", message, context),
    info: (message: string, context?: EventContext) => this.info("srs", message, context),
    warn: (message: string, context?: EventContext) => this.warn("srs", message, context),
    error: (message: string, context?: EventContext) =>
      this.error("srs", message, context),
  };
}

// Export singleton instance
export const logger = new Logger();
